import { useEffect, useState } from 'react'
import SEO from '../components/SEO.jsx'
import { api } from '../lib/api.js'
import './StatusPage.css'

const STATE_META = {
  operational: { label: 'Operational', className: 'is-operational' },
  degraded: { label: 'Degraded', className: 'is-degraded' },
  down: { label: 'Outage', className: 'is-down' },
}

function timeAgo(iso) {
  if (!iso) return ''
  const s = (Date.now() - new Date(iso).getTime()) / 1000
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return new Date(iso).toLocaleDateString()
}

function overallState(sections) {
  const items = (sections ?? []).flatMap((s) => s.items ?? [])
  if (!items.length) return { state: 'operational', label: 'All Systems Operational' }
  if (items.some((i) => i.state === 'down')) return { state: 'down', label: 'Service Disruption' }
  if (items.some((i) => i.state === 'degraded')) return { state: 'degraded', label: 'Degraded Performance' }
  return { state: 'operational', label: 'All Systems Operational' }
}

export default function StatusPage() {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    const load = () =>
      api.getStatusPage()
        .then((d) => { if (!cancelled) { setData(d); setError('') } })
        .catch((e) => { if (!cancelled && !data) setError(e.message ?? 'Could not load status.') })
    load()
    const timer = setInterval(load, 60_000)
    return () => { cancelled = true; clearInterval(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const announcement = data?.announcement
  const sections = data?.sections ?? []
  const updates = data?.updates ?? []
  const overall = overallState(sections)
  const overallMeta = STATE_META[overall.state]
  const totalItems = sections.flatMap((s) => s.items ?? []).length

  return (
    <div className="container status-page">
      <SEO title="System Status — LooksLab" description="Live availability of the LooksLab store, ordering, and fulfillment." />

      <span className="status-page__eyebrow">Live Status</span>
      <h1>System Status</h1>
      <p className="status-page__note">
        Current availability of our store, ordering, and fulfillment.
        {data?.updatedAt ? ` Last updated ${timeAgo(data.updatedAt)}.` : ''}
      </p>

      {announcement?.visible && announcement.text && (
        <div className={`status-page__announcement status-page__announcement--${announcement.style ?? 'info'}`}>
          <span className="status-page__dot" aria-hidden="true" />
          <p>{announcement.text}</p>
        </div>
      )}

      {error && !data && <p className="status-page__error">{error}</p>}

      {data && (
        <div className={`status-page__overall ${overallMeta.className}`}>
          <span className={`status-page__dot status-page__dot--lg ${overallMeta.className}`} aria-hidden="true" />
          <div>
            <h2>{overall.label}</h2>
            <p>
              {totalItems} monitored component{totalItems === 1 ? '' : 's'} across {sections.length} section
              {sections.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
      )}

      {sections.map((section) => (
        <div key={section.id} className="status-page__section">
          <div className="status-page__section-head">
            <h3>{section.title}</h3>
            {section.description && <p>{section.description}</p>}
          </div>
          {(section.items ?? []).map((item) => {
            const meta = STATE_META[item.state] ?? STATE_META.operational
            return (
              <div key={item.id} className="status-page__item">
                <span className={`status-page__dot ${meta.className}`} aria-hidden="true" />
                <div className="status-page__item-info">
                  <p className="status-page__item-name">{item.name}</p>
                  {item.description && <p className="status-page__item-desc">{item.description}</p>}
                </div>
                <div className="status-page__item-state">
                  <p className={meta.className}>{meta.label}</p>
                  {item.updatedAt && <p className="status-page__item-time">{timeAgo(item.updatedAt)}</p>}
                </div>
              </div>
            )
          })}
        </div>
      ))}

      {updates.length > 0 && (
        <div className="status-page__updates">
          <span className="status-page__updates-label">Recent Updates</span>
          {updates.map((u, i) => (
            <div key={i} className={`status-page__update status-page__update--${u.style ?? 'info'}`}>
              <div className="status-page__update-top">
                <p>{u.title}</p>
                <p className="status-page__update-time">{u.at}</p>
              </div>
              {u.body && <p className="status-page__update-body">{u.body}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
